import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, Eyebrow } from "@/components/site/Motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_EMAIL } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", subject: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-[#0B132B]">{label}</span>
    {children}
  </label>
);

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Message sent. We will get back to you soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg ?? err?.response?.data?.detail ?? "Something went wrong. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-start justify-center rounded-[2rem] border border-slate-200 bg-white p-10">
        <CheckCircle2 className="h-12 w-12 text-[#0D6EFD]" />
        <h3 className="font-display mt-6 text-3xl font-extrabold text-[#0B132B]">Thanks, message received.</h3>
        <p className="mt-3 text-slate-600">We read every message and will reply to the email you gave us.</p>
        <button data-testid="contact-send-another-button" onClick={() => setSent(false)} className="mt-8 text-sm font-bold text-[#0D6EFD]">Send another message</button>
      </div>
    );
  }

  const inputCls = "h-12 rounded-xl border-slate-200 bg-[#FAFAFC] text-base focus-visible:ring-[#0D6EFD]";
  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-7 shadow-[0_30px_80px_-40px_rgba(11,19,43,0.35)] sm:p-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Your name"><Input required maxLength={100} value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} placeholder="Jane Doe" /></Field>
        <Field label="Email"><Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} placeholder="you@example.com" /></Field>
      </div>
      <Field label="Subject (optional)"><Input maxLength={150} value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" className={inputCls} placeholder="App feedback, question, idea..." /></Field>
      <Field label="Message"><Textarea required minLength={5} maxLength={4000} rows={6} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="rounded-xl border-slate-200 bg-[#FAFAFC] text-base focus-visible:ring-[#0D6EFD]" placeholder="Tell us what's on your mind" /></Field>
      <button type="submit" disabled={sending} data-testid="contact-submit-button" className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0D6EFD] px-7 py-4 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#0B5ED7] disabled:opacity-60 sm:w-auto">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
        {sending ? "Sending..." : "Send message"}
      </button>
    </form>
  );
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact us"
        lines={["Say hello.", <span className="text-[#0D6EFD]">We read everything.</span>]}
        intro="Questions about one of our apps, feedback, or just want to follow what we build next? Send us a message."
      />
      <section className="mx-auto grid max-w-7xl gap-10 px-5 pb-28 sm:px-8 lg:grid-cols-12">
        <FadeUp className="lg:col-span-4">
          <Eyebrow>Email us directly</Eyebrow>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-direct-email-link" className="font-display mt-5 flex items-center gap-3 break-all text-2xl font-bold text-[#0B132B] transition-colors hover:text-[#0D6EFD]">
            <Mail className="h-6 w-6 shrink-0 text-[#0D6EFD]" /> {CONTACT_EMAIL}
          </a>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-600">For help with Trailer API or Trivia Pop, include the app name and the device you are using so we can help faster.</p>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-8"><ContactForm /></FadeUp>
      </section>
    </div>
  );
}
